import { createBrowserRouter, Navigate } from "react-router-dom";
import { lazy, type ComponentType } from "react";
import { RootErrorBoundary } from "./RootErrorBoundary";
import { PublicLayout } from "@/components/layout/PublicLayout";
import { PortalLayout } from "@/components/layout/PortalLayout";
import { AuthLayout } from "@/components/layout/AuthLayout";
import { PermissionGate, RequireAuth, RequireRole } from "@/components/permissions/gates";
import { NotFoundPage } from "@/components/layout/NotFoundPage";
import { AccessDeniedPage } from "@/components/layout/AccessDeniedPage";
import { LoginPage } from "@/features/auth/pages/LoginPage";
import { StubPage } from "@/components/layout/StubPage";
const lazyNamed = (loader: () => Promise<Record<string, unknown>>, name: string) =>
  lazy(async () => ({
    default: (await loader())[name] as ComponentType<Record<string, unknown>>,
  }));

const HomePage = lazyNamed(() => import("@/features/public-site/pages/HomePage"), "HomePage");
const publicPages = () => import("@/features/public-site/pages/PublicPages");
const AboutPage = lazyNamed(publicPages, "AboutPage");
const HistoryPage = lazyNamed(publicPages, "HistoryPage");
const MissionPage = lazyNamed(publicPages, "MissionPage");
const LeadershipPage = lazyNamed(publicPages, "LeadershipPage");
const TeamsPage = lazyNamed(publicPages, "TeamsPage");
const TeamDetailPage = lazyNamed(publicPages, "TeamDetailPage");
const PlayerDetailPage = lazyNamed(publicPages, "PlayerDetailPage");
const StaffPage = lazyNamed(publicPages, "StaffPage");
const FixturesPage = lazyNamed(publicPages, "FixturesPage");
const ResultsPage = lazyNamed(publicPages, "ResultsPage");
const LeagueTablesPage = lazyNamed(publicPages, "LeagueTablesPage");
const CompetitionHistoryPage = lazyNamed(publicPages, "CompetitionHistoryPage");
const NewsPage = lazyNamed(publicPages, "NewsPage");
const ArticlePage = lazyNamed(publicPages, "ArticlePage");
const VideosPage = lazyNamed(publicPages, "VideosPage");
const HighlightsPage = lazyNamed(publicPages, "HighlightsPage");
const GalleriesPage = lazyNamed(publicPages, "GalleriesPage");
const GalleryDetailPage = lazyNamed(publicPages, "GalleryDetailPage");
const SponsorsPage = lazyNamed(publicPages, "SponsorsPage");
const ContactPage = lazyNamed(publicPages, "ContactPage");
const FormPage = lazyNamed(publicPages, "FormPage");
const matchPages = () => import("@/features/public-site/pages/MatchAndWatch");
const LiveMatchPage = lazyNamed(matchPages, "LiveMatchPage");
const WatchLandingPage = lazyNamed(matchPages, "WatchLandingPage");
const WatchStreamPage = lazyNamed(matchPages, "WatchStreamPage");
import {
  ForgotPasswordPage,
  ResetPasswordPage,
  VerifyEmailPage,
} from "@/features/auth/pages/AuthMiscPages";
const adminPages = () => import("@/features/admin/AdminPages");
const AdminDashboardPage = lazyNamed(adminPages, "AdminDashboardPage");
const AdminUsersPage = lazyNamed(adminPages, "AdminUsersPage");
const AdminRolesPage = lazyNamed(adminPages, "AdminRolesPage");
const AdminPermissionsPage = lazyNamed(adminPages, "AdminPermissionsPage");
const AdminPlayersPage = lazyNamed(adminPages, "AdminPlayersPage");
const AdminFixturesPage = lazyNamed(adminPages, "AdminFixturesPage");
const AdminStreamsPage = lazyNamed(adminPages, "AdminStreamsPage");
const AdminStreamOperatorPage = lazyNamed(adminPages, "AdminStreamOperatorPage");
const AdminArticlesPage = lazyNamed(adminPages, "AdminArticlesPage");
const AdminMediaPage = lazyNamed(adminPages, "AdminMediaPage");
const AdminGalleriesPage = lazyNamed(adminPages, "AdminGalleriesPage");
const AdminSponsorsPage = lazyNamed(adminPages, "AdminSponsorsPage");
const AdminFormsPage = lazyNamed(adminPages, "AdminFormsPage");
const AdminEnquiriesPage = lazyNamed(adminPages, "AdminEnquiriesPage");
const AdminSubscribersPage = lazyNamed(adminPages, "AdminSubscribersPage");
const AdminAuditLogsPage = lazyNamed(adminPages, "AdminAuditLogsPage");
const AdminReportsPage = lazyNamed(adminPages, "AdminReportsPage");
const AdminSettingsPage = lazyNamed(adminPages, "AdminSettingsPage");
const AdminHomepageBuilderPage = lazyNamed(adminPages, "AdminHomepageBuilderPage");
const AdminMatchControlPage = lazyNamed(adminPages, "AdminMatchControlPage");
const teamStaffPages = () => import("@/features/admin/AdminTeamsStaffPages");
const AdminTeamsPage = lazyNamed(teamStaffPages, "AdminTeamsPage");
const AdminStaffPage = lazyNamed(teamStaffPages, "AdminStaffPage");
const AdminPlayerDetailPage = lazyNamed(teamStaffPages, "AdminPlayerDetailPage");
const portalPages = () => import("@/features/portals/PortalPages");
const PlayerDashboardPage = lazyNamed(portalPages, "PlayerDashboardPage");
const PlayerProfilePage = lazyNamed(portalPages, "PlayerProfilePage");
const PlayerFixturesPage = lazyNamed(portalPages, "PlayerFixturesPage");
const PlayerResultsPage = lazyNamed(portalPages, "PlayerResultsPage");
const PlayerStatisticsPage = lazyNamed(portalPages, "PlayerStatisticsPage");
const PlayerTrainingPage = lazyNamed(portalPages, "PlayerTrainingPage");
const PlayerAttendancePage = lazyNamed(portalPages, "PlayerAttendancePage");
const PlayerDocumentsPage = lazyNamed(portalPages, "PlayerDocumentsPage");
const PlayerVideosPage = lazyNamed(portalPages, "PlayerVideosPage");
const PlayerAnnouncementsPage = lazyNamed(portalPages, "PlayerAnnouncementsPage");
const PlayerNotificationsPage = lazyNamed(portalPages, "PlayerNotificationsPage");
const CoachDashboardPage = lazyNamed(portalPages, "CoachDashboardPage");
const CoachTrainingPage = lazyNamed(portalPages, "CoachTrainingPage");
const CoachAttendancePage = lazyNamed(portalPages, "CoachAttendancePage");
const CoachSelectionsPage = lazyNamed(portalPages, "CoachSelectionsPage");
const CoachAssessmentsPage = lazyNamed(portalPages, "CoachAssessmentsPage");
const CoachReportsPage = lazyNamed(portalPages, "CoachReportsPage");
const CoachTeamPage = lazyNamed(portalPages, "CoachTeamPage");
import {
  LayoutDashboard,
  Users,
  ShieldCheck,
  Trophy,
  UserSquare2,
  Users2,
  Calendar,
  Radio,
  FileText,
  ImageIcon,
  Building2,
  ClipboardList,
  Bell,
  BarChart3,
  Activity,
  Settings,
  Layers,
  PenLine,
  Grid3x3,
  Home as HomeIcon,
} from "lucide-react";

const ADMIN_NAV = [
  { label: "Dashboard", to: "", icon: LayoutDashboard, section: "Overview" },
  { label: "Homepage builder", to: "homepage-builder", icon: Layers, section: "Content" },
  { label: "Articles", to: "articles", icon: PenLine, section: "Content" },
  { label: "Media library", to: "media", icon: ImageIcon, section: "Content" },
  { label: "Galleries", to: "galleries", icon: Grid3x3, section: "Content" },
  { label: "Videos", to: "videos", icon: Radio, section: "Content" },
  { label: "Teams", to: "teams", icon: Users2, section: "Football" },
  { label: "Players", to: "players", icon: UserSquare2, section: "Football" },
  { label: "Staff", to: "staff", icon: Users, section: "Football" },
  { label: "Fixtures", to: "fixtures", icon: Calendar, section: "Football" },
  { label: "Streams", to: "streams", icon: Radio, section: "Broadcast" },
  { label: "Sponsors", to: "sponsors", icon: Building2, section: "Commercial" },
  { label: "Forms", to: "forms", icon: ClipboardList, section: "Commercial" },
  { label: "Enquiries", to: "enquiries", icon: Bell, section: "Commercial" },
  { label: "Subscribers", to: "subscribers", icon: Users, section: "Commercial" },
  { label: "Users", to: "users", icon: Users, section: "Access" },
  { label: "Roles", to: "roles", icon: ShieldCheck, section: "Access" },
  { label: "Permissions", to: "permissions", icon: ShieldCheck, section: "Access" },
  { label: "Reports", to: "reports", icon: BarChart3, section: "Insights" },
  { label: "Audit logs", to: "audit-logs", icon: Activity, section: "Insights" },
  {
    label: "Website content",
    to: "settings",
    icon: Settings,
    section: "System",
    permission: "settings.manage",
  },
];

const PLAYER_NAV = [
  { label: "Dashboard", to: "", icon: LayoutDashboard },
  { label: "My profile", to: "profile", icon: UserSquare2 },
  { label: "Fixtures", to: "fixtures", icon: Calendar },
  { label: "Results", to: "results", icon: Trophy },
  { label: "Statistics", to: "statistics", icon: BarChart3 },
  { label: "Training", to: "training", icon: Activity },
  { label: "Attendance", to: "attendance", icon: ClipboardList },
  { label: "Documents", to: "documents", icon: FileText },
  { label: "Videos", to: "videos", icon: Radio },
  { label: "Announcements", to: "announcements", icon: Bell },
  { label: "Notifications", to: "notifications", icon: Bell },
];

const COACH_NAV = [
  { label: "Dashboard", to: "", icon: LayoutDashboard },
  { label: "Team", to: "teams/team_first_sample", icon: Users2 },
  { label: "Training", to: "training", icon: Activity },
  { label: "Attendance", to: "attendance", icon: ClipboardList },
  { label: "Selections", to: "selections", icon: Trophy },
  { label: "Assessments", to: "assessments", icon: BarChart3 },
  { label: "Reports", to: "reports", icon: BarChart3 },
];

const MEDIA_NAV = [
  { label: "Dashboard", to: "", icon: LayoutDashboard },
  { label: "Library", to: "library", icon: ImageIcon },
  { label: "Galleries", to: "galleries", icon: Grid3x3 },
];

const MANAGER_NAV = [
  { label: "Dashboard", to: "", icon: LayoutDashboard },
  { label: "Squad", to: "squad", icon: UserSquare2 },
];

export const router = createBrowserRouter([
  {
    element: <PublicLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <HomePage /> },
      { path: "about", element: <AboutPage /> },
      { path: "about/history", element: <HistoryPage /> },
      { path: "about/mission-vision-values", element: <MissionPage /> },
      { path: "about/leadership", element: <LeadershipPage /> },
      { path: "teams", element: <TeamsPage /> },
      { path: "teams/:teamId", element: <TeamDetailPage /> },
      { path: "players/:playerId", element: <PlayerDetailPage /> },
      { path: "staff", element: <StaffPage /> },
      { path: "fixtures", element: <FixturesPage /> },
      { path: "results", element: <ResultsPage /> },
      { path: "matches/:matchId", element: <LiveMatchPage /> },
      { path: "league-tables", element: <LeagueTablesPage /> },
      { path: "competitions", element: <CompetitionHistoryPage /> },
      { path: "competitions/:competitionSlug/:seasonSlug", element: <CompetitionHistoryPage /> },
      {
        path: "competitions/:competitionSlug/:seasonSlug/:competitionTab",
        element: <CompetitionHistoryPage />,
      },
      { path: "watch", element: <WatchLandingPage /> },
      { path: "watch/:streamId", element: <WatchStreamPage /> },
      { path: "news", element: <NewsPage /> },
      { path: "news/:slug", element: <ArticlePage /> },
      { path: "videos", element: <VideosPage /> },
      { path: "highlights", element: <HighlightsPage /> },
      { path: "galleries", element: <GalleriesPage /> },
      { path: "galleries/:galleryId", element: <GalleryDetailPage /> },
      { path: "sponsors", element: <SponsorsPage /> },
      { path: "contact", element: <ContactPage /> },
      { path: "forms/trial", element: <FormPage formId="trial" /> },
      { path: "forms/academy", element: <FormPage formId="academy" /> },
      { path: "forms/sponsorship", element: <FormPage formId="sponsorship" /> },
      { path: "forms/media", element: <FormPage formId="media" /> },
      { path: "access-denied", element: <AccessDeniedPage /> },
    ],
  },
  {
    element: <AuthLayout />,
    errorElement: <RootErrorBoundary />,
    children: [
      { path: "auth/login", element: <LoginPage /> },
      { path: "auth/forgot-password", element: <ForgotPasswordPage /> },
      { path: "auth/reset-password", element: <ResetPasswordPage /> },
      { path: "auth/verify-email", element: <VerifyEmailPage /> },
    ],
  },
  {
    path: "admin",
    element: (
      <RequireAuth>
        <RequireRole
          roles={[
            "super_administrator",
            "club_administrator",
            "team_administrator",
            "media_manager",
            "content_editor",
            "match_operator",
            "stream_operator",
          ]}
        >
          <PortalLayout portalName="Administration" basePath="/admin" nav={ADMIN_NAV} />
        </RequireRole>
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <AdminDashboardPage /> },
      { path: "users", element: <AdminUsersPage /> },
      { path: "roles", element: <AdminRolesPage /> },
      { path: "permissions", element: <AdminPermissionsPage /> },
      { path: "teams", element: <AdminTeamsPage /> },
      { path: "players", element: <AdminPlayersPage /> },
      { path: "players/:playerId", element: <AdminPlayerDetailPage /> },
      { path: "staff", element: <AdminStaffPage /> },
      { path: "fixtures", element: <AdminFixturesPage /> },
      { path: "matches/:matchId", element: <AdminMatchControlPage /> },
      { path: "streams", element: <AdminStreamsPage /> },
      { path: "streams/:streamId", element: <AdminStreamOperatorPage /> },
      { path: "articles", element: <AdminArticlesPage /> },
      { path: "videos", element: <AdminMediaPage /> },
      { path: "media", element: <AdminMediaPage /> },
      { path: "galleries", element: <AdminGalleriesPage /> },
      { path: "sponsors", element: <AdminSponsorsPage /> },
      { path: "forms", element: <AdminFormsPage /> },
      { path: "enquiries", element: <AdminEnquiriesPage /> },
      { path: "subscribers", element: <AdminSubscribersPage /> },
      { path: "reports", element: <AdminReportsPage /> },
      { path: "analytics", element: <AdminReportsPage /> },
      { path: "audit-logs", element: <AdminAuditLogsPage /> },
      {
        path: "settings",
        element: (
          <PermissionGate permission="settings.manage" fallback={<AccessDeniedPage />}>
            <AdminSettingsPage />
          </PermissionGate>
        ),
      },
      { path: "homepage-builder", element: <AdminHomepageBuilderPage /> },
    ],
  },
  {
    path: "player",
    element: (
      <RequireAuth>
        <RequireRole roles={["player"]}>
          <PortalLayout portalName="Player Portal" basePath="/player" nav={PLAYER_NAV} />
        </RequireRole>
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <PlayerDashboardPage /> },
      { path: "profile", element: <PlayerProfilePage /> },
      { path: "fixtures", element: <PlayerFixturesPage /> },
      { path: "results", element: <PlayerResultsPage /> },
      { path: "statistics", element: <PlayerStatisticsPage /> },
      { path: "training", element: <PlayerTrainingPage /> },
      { path: "attendance", element: <PlayerAttendancePage /> },
      { path: "documents", element: <PlayerDocumentsPage /> },
      { path: "videos", element: <PlayerVideosPage /> },
      { path: "announcements", element: <PlayerAnnouncementsPage /> },
      { path: "notifications", element: <PlayerNotificationsPage /> },
    ],
  },
  {
    path: "coach",
    element: (
      <RequireAuth>
        <RequireRole roles={["head_coach", "assistant_coach"]}>
          <PortalLayout portalName="Coaching Portal" basePath="/coach" nav={COACH_NAV} />
        </RequireRole>
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <CoachDashboardPage /> },
      { path: "teams/:teamId", element: <CoachTeamPage /> },
      { path: "training", element: <CoachTrainingPage /> },
      { path: "attendance", element: <CoachAttendancePage /> },
      { path: "selections", element: <CoachSelectionsPage /> },
      { path: "assessments", element: <CoachAssessmentsPage /> },
      { path: "reports", element: <CoachReportsPage /> },
    ],
  },
  {
    path: "media",
    element: (
      <RequireAuth>
        <RequireRole roles={["media_manager", "content_editor", "photographer", "videographer"]}>
          <PortalLayout portalName="Media Portal" basePath="/media" nav={MEDIA_NAV} />
        </RequireRole>
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <AdminMediaPage /> },
      { path: "library", element: <AdminMediaPage /> },
      { path: "galleries", element: <AdminGalleriesPage /> },
    ],
  },
  {
    path: "manager",
    element: (
      <RequireAuth>
        <RequireRole roles={["team_manager"]}>
          <PortalLayout portalName="Team Manager" basePath="/manager" nav={MANAGER_NAV} />
        </RequireRole>
      </RequireAuth>
    ),
    errorElement: <RootErrorBoundary />,
    children: [
      { index: true, element: <CoachDashboardPage /> },
      { path: "squad", element: <CoachTeamPage /> },
    ],
  },
  { path: "*", element: <NotFoundPage /> },
]);
